import React, { useRef, useState } from "react";
import { ArrowLeft, Palette, Lock, LockOpen, Key, ShieldHalf } from "lucide-react";

const themeColors = [
  { name: "green", cls: "bg-green-500" },
  { name: "blue", cls: "bg-blue-500" },
  { name: "purple", cls: "bg-purple-500" },
  { name: "rose", cls: "bg-rose-500" },
  { name: "amber", cls: "bg-amber-500" },
];

const inputCls =
  "w-full bg-slate-900 border border-slate-700 focus:border-brand-500 rounded-xl px-4 py-2 text-sm text-white placeholder-slate-500 focus:outline-none transition-colors";

const submitCls =
  "w-full bg-brand-600 hover:bg-brand-500 text-white font-bold py-3 rounded-xl shadow-lg shadow-brand-600/30 transition-colors mt-3 active:scale-95 text-xs flex items-center justify-center gap-2";

export default function SettingsView({
  hasPassword,
  onBack,
  onApplyTheme,
  onCreatePassword,
  onChangePassword,
  verifyOldPassword,
  notify,
  apiRef,
}) {
  const [step, setStep] = useState(1);
  const otpRef = useRef("");
  const tempRef = useRef({});

  const [regPhone, setRegPhone] = useState("");
  const [regPass, setRegPass] = useState("");
  const [regPassConfirm, setRegPassConfirm] = useState("");
  const [regOtp, setRegOtp] = useState("");

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");

  const create1Ref = useRef(null);
  const create2Ref = useRef(null);
  const changeRef = useRef(null);

  React.useImperativeHandle(
    apiRef,
    () => ({
      submitVisible: () => {
        const form = hasPassword ? changeRef.current : step === 1 ? create1Ref.current : create2Ref.current;
        if (form) form.requestSubmit();
      },
    }),
    [hasPassword, step]
  );

  const handleCreateStep1 = (e) => {
    e.preventDefault();
    if (regPass !== regPassConfirm) {
      notify("Hata", "Girdiğiniz şifreler uyuşmuyor.", "error", false);
      return;
    }
    if (regPass.length < 4) {
      notify("Uyarı", "Şifreniz en az 4 karakter olmalıdır.", "info", false);
      return;
    }
    tempRef.current = { phone: regPhone.trim(), pass: regPass };
    otpRef.current = Math.floor(100000 + Math.random() * 900000).toString();
    setStep(2);
    notify(
      "SMS Gönderildi",
      `(Simülasyon)\nTelefonunuza (${regPhone.trim()}) gönderilen doğrulama kodu:\n\n${otpRef.current}`,
      "info",
      false
    );
  };

  const handleCreateStep2 = (e) => {
    e.preventDefault();
    if (regOtp.trim() === otpRef.current) {
      onCreatePassword(tempRef.current.phone, tempRef.current.pass);
      setRegPhone("");
      setRegPass("");
      setRegPassConfirm("");
      setRegOtp("");
      setStep(1);
    } else {
      notify("Hata", "Girdiğiniz doğrulama kodu hatalı.", "error", false);
    }
  };

  const handlePasswordChange = (e) => {
    e.preventDefault();
    if (!verifyOldPassword(oldPassword)) {
      notify("Hata", "Girdiğiniz eski şifre yanlış. Lütfen tekrar deneyin.", "error", false);
      return;
    }
    if (newPassword !== newPasswordConfirm) {
      notify("Hata", "Yeni şifreler birbiriyle uyuşmuyor.", "error", false);
      return;
    }
    if (newPassword.length < 4) {
      notify("Uyarı", "Şifreniz en az 4 karakter olmalıdır.", "info", false);
      return;
    }
    onChangePassword(newPassword);
    setOldPassword("");
    setNewPassword("");
    setNewPasswordConfirm("");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-2">
        <button
          onClick={onBack}
          className="text-xs bg-slate-800 text-slate-300 hover:text-white px-3 py-1.5 rounded-lg border border-slate-700 flex items-center gap-1.5 transition-colors"
        >
          <ArrowLeft size={12} /> Geri Dön
        </button>
        <h2 className="text-white font-bold text-sm bg-slate-800 px-3 py-1 rounded-lg border border-slate-700">Ayarlar</h2>
      </div>

      <div className="bg-slate-800/90 border border-slate-700 p-5 rounded-2xl shadow-xl space-y-4 relative overflow-hidden">
        <div className="absolute -right-4 -top-4 text-brand-500/10 pointer-events-none">
          <Palette size={90} strokeWidth={1.2} />
        </div>
        <h3 className="text-sm font-bold text-white flex items-center gap-2 relative z-10">
          <Palette size={15} className="text-brand-400" /> Tema Rengi
        </h3>
        <div className="flex gap-4 justify-center relative z-10 py-2">
          {themeColors.map((t) => (
            <button
              key={t.name}
              onClick={() => onApplyTheme(t.name)}
              className={`w-10 h-10 rounded-full ${t.cls} shadow-md border-2 border-slate-600 hover:scale-110 focus:border-white transition-all`}
            />
          ))}
        </div>
      </div>

      <div className="bg-slate-800/90 border border-slate-700 p-5 rounded-2xl shadow-xl space-y-4 relative overflow-hidden">
        <div className="absolute -right-4 -bottom-4 text-brand-500/10 pointer-events-none">
          <ShieldHalf size={110} strokeWidth={1.2} />
        </div>

        <h3 className="text-sm font-bold text-white flex items-center gap-2 relative z-10">
          {hasPassword ? (
            <>
              <Lock size={15} className="text-brand-400" /> Şifre Değiştir
            </>
          ) : (
            <>
              <LockOpen size={15} className="text-brand-400" /> Şifre Oluştur
            </>
          )}
        </h3>

        {!hasPassword && step === 1 && (
          <form ref={create1Ref} onSubmit={handleCreateStep1} className="space-y-3 relative z-10">
            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1">Telefon Numaranız (Sıfırlama için)</label>
              <input
                type="tel"
                required
                value={regPhone}
                onChange={(e) => setRegPhone(e.target.value)}
                placeholder="05XX XXX XX XX"
                className={`${inputCls} tracking-widest text-center`}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1">Yeni Şifre</label>
              <input
                type="password"
                required
                value={regPass}
                onChange={(e) => setRegPass(e.target.value)}
                className={`${inputCls} tracking-[0.3em] text-center`}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1">Şifre Tekrar</label>
              <input
                type="password"
                required
                value={regPassConfirm}
                onChange={(e) => setRegPassConfirm(e.target.value)}
                className={`${inputCls} tracking-[0.3em] text-center`}
              />
            </div>
            <button type="submit" className={submitCls}>
              Şifre Oluştur (Kod Gönder)
            </button>
          </form>
        )}

        {!hasPassword && step === 2 && (
          <form ref={create2Ref} onSubmit={handleCreateStep2} className="space-y-3 relative z-10">
            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1 text-center">SMS Doğrulama Kodu</label>
              <input
                type="number"
                required
                value={regOtp}
                onChange={(e) => setRegOtp(e.target.value)}
                placeholder="6 Haneli Kod"
                className="w-full bg-slate-900 border border-slate-700 focus:border-brand-500 rounded-xl px-4 py-3 text-lg text-brand-400 placeholder-slate-500 focus:outline-none transition-colors font-mono tracking-widest text-center"
              />
            </div>
            <button type="submit" className={submitCls}>
              Kodu Onayla ve Şifreyi Aktifleştir
            </button>
            <button
              type="button"
              onClick={() => {
                setStep(1);
                setRegOtp("");
              }}
              className="w-full text-slate-400 hover:text-white text-xs mt-2 transition-colors"
            >
              İptal Et
            </button>
          </form>
        )}

        {hasPassword && (
          <form ref={changeRef} onSubmit={handlePasswordChange} className="space-y-3 relative z-10">
            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1">Eski Şifre</label>
              <input
                type="password"
                required
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                placeholder="Mevcut şifreniz"
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1">Yeni Şifre</label>
              <input
                type="password"
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Yeni şifrenizi girin"
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1">Yeni Şifre (Tekrar)</label>
              <input
                type="password"
                required
                value={newPasswordConfirm}
                onChange={(e) => setNewPasswordConfirm(e.target.value)}
                placeholder="Yeni şifrenizi tekrar girin"
                className={inputCls}
              />
            </div>
            <button type="submit" className={submitCls}>
              <Key size={13} /> Şifreyi Güncelle
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
